import copy from '../../copy/en/auth.json' with { type: 'json' };
/**
 * Loads `GET /api/me` for every authenticated route and exposes the staff
 * member (email, role, capabilities, MFA status). Capability checks in the UI
 * mirror this payload; the server remains authoritative.
 */
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import type { PortalCapability } from "../../shared/roles.js";
import { isApiError } from "../lib/api.js";
import { normalizeMe, type Me } from "../lib/me.js";
import { useSession } from "./SessionProvider.js";
import { AccessDeniedScreen, FullPageError, FullPageSpinner } from "../components/ui/FullPage.js";
import { Navigate } from "react-router";

export interface MeContextValue {
  /** Null only while `/me` itself is blocked by `mfa_required`. */
  me: Me | null;
  /** `/me` answered 403 mfa_required — the whole API is gated until aal2. */
  mfaBlocked: boolean;
  refetch: () => Promise<unknown>;
  hasCap: (cap: PortalCapability) => boolean;
}

/** Exported for tests that need to inject a fake `me` under a guard. */
export const MeContext = createContext<MeContextValue | null>(null);

export const ME_QUERY_KEY = ["me"] as const;

export function MeProvider({ children }: { children: ReactNode }) {
  const { api, signOut } = useSession();
  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => normalizeMe(await api.get<unknown>("/me")),
    retry: false,
    staleTime: 60_000,
  });

  if (query.isPending) return <FullPageSpinner label={copy.MeProvider.loadingYourSession} />;

  if (query.isError) {
    const err = query.error;
    if (isApiError(err) && err.code === "mfa_required") {
      const value: MeContextValue = {
        me: null,
        mfaBlocked: true,
        refetch: query.refetch,
        hasCap: () => false,
      };
      return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
    }
    if (isApiError(err) && err.status === 403) {
      if (err.code === "not_provisioned" || err.code === "onboarding_required") return <Navigate to="/join" replace />;
      const message =
        err.code === "disabled"
          ? copy.MeProvider.thisAccountHasBeenDisabledContactAn
          : err.code === "provisioning_incomplete"
          ? copy.MeProvider.yourStaffAccountIsStillBeingSet
          : copy.MeProvider.thisAccountIsNotProvisionedForThe;
      return <AccessDeniedScreen message={message} onSignOut={() => void signOut(copy.MeProvider.youHaveBeenSignedOut)} />;
    }
    if (isApiError(err) && err.status === 401) {
      // The API client already cleared the session; the guard will redirect.
      return <FullPageSpinner label={copy.MeProvider.signingOut} />;
    }
    return (
      <FullPageError
        title={copy.MeProvider.couldNotLoadYourSession}
        message={copy.MeProvider.thePortalApiDidNotRespondCheck}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const me = query.data;
  const value: MeContextValue = {
    me,
    mfaBlocked: false,
    refetch: query.refetch,
    hasCap: (cap) => me.capabilities.includes(cap),
  };
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeContextValue {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMe must be used inside <MeProvider>");
  return ctx;
}
